import React from "react";
import {
  Modal,
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import {
  barDayColumns,
  barWeeklyColumns,
  barMonthColumns,
  TIME_FRAMES,
} from "./columns";
import LocalBarIcon from "@mui/icons-material/LocalBar";

import { format, parseISO } from "date-fns";
import { reportAdminModalStyles } from "../../components/reportStyles";
import { getFormattedWeek } from "./Excel";

const BarDetailsModal = ({ open, onClose, barData, currentTimeFrame }) => {
  if (!barData) return null;

  let reportData = [];

  if (
    currentTimeFrame === TIME_FRAMES.DAY &&
    Array.isArray(barData.dayWiseReport)
  ) {
    reportData = barData.dayWiseReport;
  } else if (
    currentTimeFrame === TIME_FRAMES.WEEK &&
    Array.isArray(barData.weekWiseReport)
  ) {
    reportData = barData.weekWiseReport;
  } else if (
    currentTimeFrame === TIME_FRAMES.MONTH &&
    Array.isArray(barData.monthWiseReport)
  ) {
    reportData = barData.monthWiseReport;
  }

  const columns =
    currentTimeFrame === TIME_FRAMES.DAY
      ? barDayColumns
      : currentTimeFrame === TIME_FRAMES.WEEK
      ? barWeeklyColumns
      : barMonthColumns;

  // Get the time frame display text
  const getTimeFrameText = () => {
    switch (currentTimeFrame) {
      case TIME_FRAMES.DAY:
        return "Daily";
      case TIME_FRAMES.WEEK:
        return "Weekly";
      case TIME_FRAMES.MONTH:
        return "Monthly";
      default:
        return "";
    }
  };

  const barName = barData.Name || "N/A";

  return (
    <Modal open={open} onClose={onClose} closeAfterTransition>
      <Box sx={reportAdminModalStyles.barModalStyle}>
        {/* Header */}
        <Box sx={reportAdminModalStyles.wholesalerHeaderBox}>
          <Box sx={reportAdminModalStyles.wholesalerHeaderBoxFlex}>
            <Typography
              variant="h5"
              fontWeight={600}
              color="#06214b"
              sx={reportAdminModalStyles.wholesalerHeaderBoxLine}
            >
              {getTimeFrameText()} Bar Transactions
            </Typography>

            <Box sx={reportAdminModalStyles.headerBoxDisplay}>
              <LocalBarIcon sx={reportAdminModalStyles.iconStyle} />
              <Typography
                variant="body2"
                color="text.secondary"
                fontWeight={500}
                sx={reportAdminModalStyles.headerBoxTextTransform}
              >
                <Chip
                  label={barName}
                  sx={reportAdminModalStyles.chipStyle}
                  title={barName}
                />{" "}
              </Typography>
            </Box>
          </Box>

          <IconButton onClick={onClose} sx={reportAdminModalStyles.closeIcon}>
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Content */}
        <Box sx={reportAdminModalStyles.contentBoxStyle}>
          <TableContainer
            component={Paper}
            elevation={0}
            sx={reportAdminModalStyles.tableContainer}
          >
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  {columns.map((column) => (
                    <TableCell
                      key={column.field}
                      sx={reportAdminModalStyles.tableStyle}
                    >
                      {column.headerName}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>

              <TableBody>
                {reportData.length > 0 ? (
                  reportData.map((report, index) => (
                    <TableRow
                      key={index}
                      sx={reportAdminModalStyles.tableBodyRow}
                    >
                      {currentTimeFrame === TIME_FRAMES.MONTH && (
                        <>
                          <TableCell sx={reportAdminModalStyles.fontWeight}>
                            {report.month
                              ? format(
                                  parseISO(`${report.month}-01`),
                                  "MMMM yyyy"
                                )
                              : "N/A"}
                          </TableCell>
                          <TableCell>
                            {report.mostOrderedBrand || "N/A"}
                          </TableCell>
                          <TableCell>
                            <Box
                              component="span"
                              sx={reportAdminModalStyles.tableCellMonth}
                            >
                              {report.totalShots}
                            </Box>
                          </TableCell>
                        </>
                      )}

                      {currentTimeFrame === TIME_FRAMES.WEEK && (
                        <>
                          <TableCell sx={reportAdminModalStyles.fontWeight}>
                            {getFormattedWeek(report.week)}
                          </TableCell>
                          <TableCell>
                            {report.mostOrderedBrand || "N/A"}
                          </TableCell>
                          <TableCell>
                            <Box
                              component="span"
                              sx={reportAdminModalStyles.tableCellWeek}
                            >
                              {report.totalShots}
                            </Box>
                          </TableCell>
                        </>
                      )}

                      {currentTimeFrame === TIME_FRAMES.DAY && (
                        <>
                          <TableCell sx={reportAdminModalStyles.fontWeight}>
                            {report.date
                              ? format(parseISO(report.date), "dd MMM yyyy")
                              : "N/A"}
                          </TableCell>
                          <TableCell>
                            {report.mostOrderedBrand || "N/A"}
                          </TableCell>
                          <TableCell>
                            <Box
                              component="span"
                              sx={reportAdminModalStyles.tableCellBrand}
                            >
                              {report.totalShots}
                            </Box>
                          </TableCell>
                        </>
                      )}
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell
                      colSpan={columns.length}
                      align="center"
                      sx={reportAdminModalStyles.tableCellStyles}
                    >
                      No transaction data available for this{" "}
                      {getTimeFrameText().toLowerCase()} period
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </Box>
    </Modal>
  );
};

export default BarDetailsModal;
